import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';
import { api } from '@/lib/api';
import type { QueueResponse } from '@/hooks/useApproval';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Button } from '@rhpc/ui';
import { useAuthStore } from '@/stores/authStore';
import { ClaimTypeBadge } from '../ClaimsPage';
import { plural } from '@/lib/plural';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    month: 'short', year: 'numeric',
  });
}

export function ExportClaimsPage() {
  const isStaff = useAuthStore((state) => state.user?.role === 'STAFF');
  const [page, setPage] = useState(1);
  const [monthFilter, setMonthFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(new Set());

  // Fetch fully approved claims from the reporting API, independently of approval queues.
  const { data, isLoading, error } = useQuery<QueueResponse>({
    queryKey: ['reports', 'claims', { page, monthFilter, searchFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ page: page.toString(), limit: '50' });
      if (monthFilter) params.append('month', monthFilter);
      if (searchFilter) params.append('search', searchFilter);
      return api.get(`/reports/claims?${params.toString()}`);
    },
  });

  const exportMutation = useMutation({
    mutationFn: async (claimIds: string[]) => {
      const response = await api.postBlob('/reports/export-claims', { claimIds });
      // Create a blob URL and trigger download
      const { blob, contentDisposition } = response;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      let filename = 'claims_export.zip';
      if (contentDisposition && contentDisposition.includes('filename=')) {
        filename = contentDisposition.split('filename=')[1]?.replace(/"/g, '') || filename;
      } else if (claimIds.length === 1) {
        filename = `Export_${Date.now()}.xlsx`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });

  // Checkbox handlers
  const toggleClaim = (id: string) => {
    setSelectedClaimIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!data) return;
    if (selectedClaimIds.size === data.claims.length) {
      setSelectedClaimIds(new Set()); // Deselect all
    } else {
      setSelectedClaimIds(new Set(data.claims.map(c => c.id))); // Select all current page
    }
  };

  const isAllSelected = data && data.claims.length > 0 && selectedClaimIds.size === data.claims.length;

  return (
    <PageShell>
      <PageHeader
        title={isStaff ? 'Export My Claims' : 'Export Claims'}
        description={
          isStaff
            ? 'Download Excel copies of your fully approved claims. Official accounting exports are prepared by Finance.'
            : 'Select fully approved claims to generate official Excel accounting reports.'
        }
        actions={
          <Button
            onClick={() => exportMutation.mutate(Array.from(selectedClaimIds))}
            disabled={selectedClaimIds.size === 0 || exportMutation.isPending}
            className="gap-2"
          >
            {exportMutation.isPending ? 'Generating...' : `Export ${plural(selectedClaimIds.size, 'Claim')}`}
          </Button>
        }
      />

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-surface p-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-semibold text-text-2 mb-1">
            {isStaff ? 'Search Ref NO' : 'Search User or Ref NO'}
          </label>
          <input
            type="text"
            placeholder={isStaff ? 'Search by reference...' : 'Search by name, email or reference...'}
            value={searchFilter}
            onChange={(e) => { setSearchFilter(e.target.value); setPage(1); }}
            className="w-full text-sm px-3 py-2 rounded-lg border border-border bg-surface focus:outline-2 focus:ring-primary/50 text-text"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-2 mb-1">Claim Month</label>
          <input
            type="month"
            value={monthFilter}
            onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}
            className="text-sm px-3 py-2 rounded-lg border border-border bg-surface text-text focus:outline-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse rounded-2xl bg-surface-2 h-16" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-danger-soft border border-danger/20 px-4 py-3 text-sm text-danger">
          Failed to load approved claims.
        </div>
      )}

      {/* Table / List */}
      {!isLoading && data && data.claims.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface/50 p-14 text-center space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-text-2">
            <FileSpreadsheet className="h-6 w-6" strokeWidth={2} />
          </div>
          <h2 className="text-lg font-semibold text-text">No approved claims ready</h2>
          <p className="text-sm text-text-2">
            {isStaff
              ? 'Once a claim of yours is fully approved, you can download its Excel report here.'
              : 'Approve claims in the queue first before exporting them here.'}
          </p>
        </div>
      ) : (
        data && data.claims.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-border bg-surface-2/50 text-xs text-text-2">
                  <th className="p-4 w-12">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded border-border" 
                      checked={!!isAllSelected}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="font-semibold p-4 whitespace-nowrap">Ref #</th>
                  <th className="font-semibold p-4 hidden lg:table-cell">Type</th>
                  <th className="font-semibold p-4">User</th>
                  <th className="font-semibold p-4">Month</th>
                  <th className="font-semibold p-4 whitespace-nowrap">Amount</th>
                  <th className="font-semibold p-4 hidden lg:table-cell">Approved Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {data.claims.map(claim => (
                  <tr key={claim.id} className={`transition-colors ${selectedClaimIds.has(claim.id) ? 'bg-primary/5' : 'hover:bg-surface-2'}`}>
                    <td className="p-4">
                      <input 
                         type="checkbox" 
                         className="w-4 h-4 rounded border-border" 
                         checked={selectedClaimIds.has(claim.id)}
                         onChange={() => toggleClaim(claim.id)}
                      />
                    </td>
                    <td className="p-4 font-mono text-xs text-primary whitespace-nowrap">{claim.referenceNumber ?? 'N/A'}</td>
                    <td className="p-4 hidden lg:table-cell">
                      <ClaimTypeBadge type={claim.claimType} />
                    </td>
                    <td className="p-4">
                      <p className="whitespace-nowrap font-medium text-text">{claim.user.name}</p>
                      <p className="hidden text-xs text-text-2 xl:block">{claim.user.email}</p>
                    </td>
                    <td className="p-4">
                      <p className="whitespace-nowrap text-text">{formatMonth(claim.claimMonth)}</p>
                      {claim.description && (
                        <p className="hidden max-w-[180px] truncate text-xs text-text-2 xl:block" title={claim.description}>
                          {claim.description}
                        </p>
                      )}
                    </td>
                    <td className="p-4 font-semibold text-text whitespace-nowrap">{formatCurrency(claim.totalAmount)}</td>
                    <td className="p-4 text-text-2 hidden lg:table-cell">{formatDate(claim.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Pagination */}
      {data && data.meta.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm pt-4 border-t border-border">
          <p className="text-text-2">
            Showing page {page} of {data.meta.pagination.totalPages} ({plural(data.meta.pagination.total, 'claim')} total)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-border font-medium bg-surface hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(data.meta.pagination.totalPages, p + 1))}
              disabled={page === data.meta.pagination.totalPages}
              className="px-3 py-1.5 rounded-lg border border-border font-medium bg-surface hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
